import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    RadioGroup,
    RadioGroupItem,
} from '@/components/ui/radio-group';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { usePresetsStore } from '@/stores/presets';
import { useCollections } from '@/hooks/useCollections';
import { useFields } from '@/hooks/useFields';
import { useRoles } from '@/hooks/access-control/useRoles';
import { useUsers } from '@/hooks/useUsers';
import { getAvailableLayouts } from '@/layouts';
import type { LayoutQuery, Preset } from '@/types/presets';
import { FilterBuilder, type Filter } from '@/components/filters';
import { toast } from 'sonner';
import { Loader2, Trash2 } from 'lucide-react';
import { api, type ApiResponse } from '@/lib/api';

function parseList(input: string): string[] | undefined {
    const cleaned = input.split(',').map((s) => s.trim()).filter(Boolean);
    return cleaned.length ? cleaned : undefined;
}

export function PresetDetailPage() {
    const { id } = useParams<{ id: string }>();
    const isNew = id === '+';
    const navigate = useNavigate();

    const { collectionPresets, hydrate, hydrated, loading, savePreset, delete: deletePreset } = usePresetsStore();
    const [fetchedPreset, setFetchedPreset] = useState<Preset | null>(null);

    const existing = useMemo(() => {
        if (!id || isNew) return null;
        return collectionPresets.find((p) => p.id === Number(id)) || fetchedPreset || null;
    }, [collectionPresets, id, isNew, fetchedPreset]);

    const [collection, setCollection] = useState(existing?.collection || '');
    const [bookmark, setBookmark] = useState(existing?.bookmark || '');
    const [icon, setIcon] = useState(existing?.icon || 'bookmark');
    const [color, setColor] = useState(existing?.color || '');
    const [scope, setScope] = useState<'global' | 'role' | 'user'>(existing?.user ? 'user' : existing?.role ? 'role' : 'global');
    const [userId, setUserId] = useState(existing?.user || '');
    const [roleId, setRoleId] = useState(existing?.role || '');
    const [layout, setLayout] = useState(existing?.layout || 'tabular');
    const [search, setSearch] = useState(existing?.search || '');
    const [filter, setFilter] = useState<Filter | null>((existing?.filter as Filter) || null);
    const [layoutQuery, setLayoutQuery] = useState<Record<string, LayoutQuery>>(existing?.layout_query || {});
    const [fieldsInput, setFieldsInput] = useState('');
    const [sortInput, setSortInput] = useState('');
    const [limitInput, setLimitInput] = useState<number | ''>('');
    const [confirmDelete, setConfirmDelete] = useState(false);

    const { data: collectionsData, isLoading: collectionsLoading } = useCollections();
    const rolesQuery = useRoles();
    const { data: usersData } = useUsers({ limit: -1, sort: ['email'] });

    const { fields, loading: fieldsLoading } = useFields({
        collection: collection || null,
        includeHidden: true,
    });

    // Sync form state when preset loads (after hydrate) or when navigating between presets
    useEffect(() => {
        if (!existing) return;

        setCollection(existing.collection || '');
        setBookmark(existing.bookmark || '');
        setIcon(existing.icon || 'bookmark');
        setColor(existing.color || '');
        setScope(existing.user ? 'user' : existing.role ? 'role' : 'global');
        setUserId(existing.user || '');
        setRoleId(existing.role || '');
        setLayout(existing.layout || 'tabular');
        setSearch(existing.search || '');
        setFilter((existing.filter as Filter) || null);
        setLayoutQuery(existing.layout_query || {});
    }, [existing]);

    useEffect(() => {
        if (!hydrated && !loading) {
            hydrate().catch(() => { });
        }
    }, [hydrated, loading, hydrate]);

    useEffect(() => {
        const fetchPreset = async () => {
            if (isNew || !id) return;
            if (existing) return;
            try {
                const response = await api.get<ApiResponse<Preset>>(`/presets/${id}`);
                setFetchedPreset(response.data);
            } catch (error) {
                console.error('Failed to fetch preset', error);
            }
        };

        fetchPreset();
    }, [isNew, id, existing]);

    useEffect(() => {
        // populate layout inputs from current layoutQuery
        const current = layoutQuery[layout];
        setFieldsInput(current?.fields?.join(', ') || '');
        setSortInput(current?.sort?.join(', ') || '');
        setLimitInput(current?.limit ?? '');
    }, [layout, layoutQuery]);

    const handleSave = async () => {
        if (!collection) {
            toast.error('Collection is required');
            return;
        }

        const targetLayoutQuery: LayoutQuery = {
            ...layoutQuery[layout],
            page: layoutQuery[layout]?.page ?? 1,
            fields: parseList(fieldsInput),
            sort: parseList(sortInput),
            limit: limitInput === '' ? undefined : Number(limitInput),
        };

        const updatedLayoutQuery = { ...layoutQuery, [layout]: targetLayoutQuery };

        const payload: Partial<Preset> = {
            id: existing?.id,
            bookmark: bookmark || null,
            icon: icon || 'bookmark',
            color: color || null,
            collection,
            search: search || null,
            filter: (filter as any) || null,
            layout,
            layout_query: updatedLayoutQuery,
            layout_options: existing?.layout_options || null,
            user: scope === 'user' ? (userId || null) : null,
            role: scope === 'role' ? (roleId || null) : null,
        };

        try {
            await savePreset(payload);
            toast.success(isNew ? 'Preset created' : 'Preset updated');
            navigate('/admin/settings/presets');
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to save preset';
            toast.error(message);
        }
    };

    const handleDelete = async () => {
        if (!existing?.id) return;
        await deletePreset([existing.id]);
        toast.success('Preset deleted');
        navigate('/admin/settings/presets');
    };

    const layoutChoices = getAvailableLayouts();

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold">{isNew ? 'Create Preset' : 'Edit Preset'}</h2>
                    <p className="text-sm text-muted-foreground">Configure layout, filters, and scope for this preset.</p>
                </div>
                <div className="flex items-center gap-2">
                    {!isNew && (
                        <Button variant="ghost" className="text-destructive" onClick={() => setConfirmDelete(true)}>
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                        </Button>
                    )}
                    <Button variant="outline" onClick={() => navigate('/admin/settings/presets')}>Cancel</Button>
                    <Button onClick={handleSave}>Save</Button>
                </div>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Preset Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Bookmark Name</Label>
                            <Input value={bookmark} onChange={(e) => setBookmark(e.target.value)} placeholder="Optional name" />
                        </div>
                        <div className="space-y-2">
                            <Label>Collection</Label>
                            <Select value={collection} onValueChange={setCollection} disabled={collectionsLoading}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select collection" />
                                </SelectTrigger>
                                <SelectContent>
                                    {(collectionsData || []).map((col) => (
                                        <SelectItem key={col.collection} value={col.collection}>
                                            {col.collection}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Icon</Label>
                            <Input value={icon} onChange={(e) => setIcon(e.target.value)} placeholder="bookmark" />
                        </div>
                        <div className="space-y-2">
                            <Label>Color</Label>
                            <Input value={color} onChange={(e) => setColor(e.target.value)} placeholder="Optional color" />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label>Scope</Label>
                        <RadioGroup className="flex flex-wrap gap-4" value={scope} onValueChange={(val: 'global' | 'role' | 'user') => setScope(val)}>
                            <div className="flex items-center space-x-2">
                                <RadioGroupItem value="global" id="scope-global" />
                                <Label htmlFor="scope-global">Global</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                                <RadioGroupItem value="role" id="scope-role" />
                                <Label htmlFor="scope-role">Role</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                                <RadioGroupItem value="user" id="scope-user" />
                                <Label htmlFor="scope-user">User</Label>
                            </div>
                        </RadioGroup>

                        {scope === 'role' && (
                            <div className="mt-2 space-y-2">
                                <Label>Role</Label>
                                <Select value={roleId} onValueChange={setRoleId}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select role" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {(rolesQuery.data || []).map((role) => (
                                            <SelectItem key={role.id} value={role.id}>
                                                {role.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {scope === 'user' && (
                            <div className="mt-2 space-y-2">
                                <Label>User</Label>
                                <Select value={userId} onValueChange={setUserId}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select user" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Array.isArray(usersData?.data) && usersData.data.map((u) => (
                                            <SelectItem key={u.id} value={u.id}>
                                                {u.email || u.id}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Layout</Label>
                            <Select value={layout} onValueChange={setLayout}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {layoutChoices.map((layoutDef) => (
                                        <SelectItem key={layoutDef.id} value={layoutDef.id}>{layoutDef.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Search</Label>
                            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Optional search term" />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                            <Label>Fields (comma separated)</Label>
                            <Input value={fieldsInput} onChange={(e) => setFieldsInput(e.target.value)} placeholder="field_a, field_b" />
                        </div>
                        <div className="space-y-2">
                            <Label>Sort (comma separated)</Label>
                            <Input value={sortInput} onChange={(e) => setSortInput(e.target.value)} placeholder="-date_created" />
                        </div>
                        <div className="space-y-2">
                            <Label>Limit</Label>
                            <Input
                                type="number"
                                value={limitInput}
                                onChange={(e) => setLimitInput(e.target.value ? Number(e.target.value) : '')}
                                min={1}
                                placeholder="25"
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label>Filter</Label>
                        {fieldsLoading && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Loading fields...
                            </div>
                        )}
                        <FilterBuilder value={filter} onChange={(val) => setFilter(val)} fields={fields} collection={collection || null} />
                    </div>
                </CardContent>
            </Card>

            <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete preset?</AlertDialogTitle>
                        <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
